import { ChevronRight, Close, OpenInNew } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import { CopyButton } from "@components/CopyButton/CopyButton";

import { EXPIRING_SOON_DAYS, getAccessRequestUrl } from "@helpers/constants";
import { formatDateTimeString, formatDistance } from "@helpers/utils";

import { getResourceLink } from "./data/resourceLinks";
import { parseResourceFromExpression, type Rule } from "./data/types";
import GrantTeamMembers, {
  type MembershipRef,
  useGrantTeam,
} from "./GrantTeamMembers";

const DRAWER_WIDTH = 480;
const DAY_MS = 24 * 60 * 60 * 1000;

const Mono = styled("span")({
  fontFamily: "monospace",
});

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const DrawerHeader = styled(Section)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

const HeaderText = styled(Box)({
  flexGrow: 1,
  minWidth: 0,
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 700,
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.5),
}));

// The grant chain, laid out as pill segments joined by chevrons. Wraps so a long
// team/role/policy chain doesn't overflow the fixed-width drawer.
const ChainRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(0.75),
}));

const ChainPill = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  fontSize: "0.8rem",
  maxWidth: "100%",
  overflowWrap: "anywhere",
}));

const ChainChevron = styled(ChevronRight)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const AttrRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `${theme.spacing(14)} 1fr`,
  gap: theme.spacing(2),
  padding: theme.spacing(1, 0),
  alignItems: "baseline",
}));

// Keeps a value and its copy button on one line without the button pushing the
// value's wrapping around.
const ValueWithCopy = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

const Footer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: "auto",
}));

const ActionTitle = styled("h2")(({ theme }) => ({
  ...theme.typography.h5,
  fontFamily: "monospace",
  color: theme.palette.text.primary,
  margin: 0,
}));

const ExpressionText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontFamily: "monospace",
  overflowWrap: "anywhere",
}));

const ExpiresDistance = styled("span")<{ soon?: boolean; expired?: boolean }>(
  ({ theme, soon, expired }) => ({
    color: expired
      ? theme.palette.error.main
      : soon
        ? theme.palette.warning.main
        : theme.palette.text.secondary,
  }),
);

const StyledDrawer = styled(Drawer)({
  "& .MuiDrawer-paper": {
    width: DRAWER_WIDTH,
    display: "flex",
    flexDirection: "column",
  },
});

type ChainNode = { label: string; mono?: boolean };

// The chain of intermediaries a grant flows through, from the user to the
// policy. The shape depends on the source:
//   direct    → user → direct policy → policy
//   role:user → user → role → policy                (source.id is the role)
//   team      → user → team → policy                (source.id is the team)
//   role:team → user → team → role → policy         (source.id is the role)
// role:team policies don't name the team, so the pill text arrives resolved by
// useGrantTeam — falling back to a plain "team" pill when it can't be pinned.
function buildChain(
  rule: Rule,
  userLabel: string,
  teamLabel: string,
): ChainNode[] {
  const policyNode: ChainNode = {
    label: `policy: ${rule.policyId}`,
    mono: true,
  };
  const user: ChainNode = { label: userLabel };
  switch (rule.source.kind) {
    case "direct":
      return [user, { label: "direct policy" }, policyNode];
    case "role":
      return [user, { label: `role: ${rule.source.id ?? "—"}` }, policyNode];
    case "team": {
      if (rule.source.viaTeamRole) {
        return [
          user,
          { label: teamLabel },
          { label: `role: ${rule.source.id ?? "—"}` },
          policyNode,
        ];
      }
      return [user, { label: teamLabel }, policyNode];
    }
    default:
      return [user, policyNode];
  }
}

function GrantChain({
  rule,
  userLabel,
  teamLabel,
}: {
  rule: Rule;
  userLabel: string;
  teamLabel: string;
}) {
  const nodes = buildChain(rule, userLabel, teamLabel);
  return (
    <ChainRow>
      {nodes.map((node, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChainChevron fontSize="small" />}
          <ChainPill>
            {node.mono ? <Mono>{node.label}</Mono> : node.label}
          </ChainPill>
        </React.Fragment>
      ))}
    </ChainRow>
  );
}

function ExpiresValue({ expires }: { expires: string | null }) {
  if (!expires) {
    return <Typography variant="body2">Never</Typography>;
  }
  const msUntilExpiry = new Date(expires).getTime() - Date.now();
  const expired = msUntilExpiry <= 0;
  const soon = !expired && msUntilExpiry <= EXPIRING_SOON_DAYS * DAY_MS;
  return (
    <Typography variant="body2">
      {formatDateTimeString(expires)}{" "}
      <ExpiresDistance soon={soon} expired={expired}>
        ({formatDistance(expires)})
      </ExpiresDistance>
    </Typography>
  );
}

type Props = {
  rule: Rule | null;
  onClose: () => void;
  canViewResource: boolean;
  userLabel: string;
  canRequestExtension: boolean;
  /** The listed user's teams, used to resolve which one a role grant came through. */
  userTeams?: MembershipRef[];
};

// The deepest level of detail on the permissions page: everything about one
// rule — how the user got it (grant chain), its policy attributes, and the
// actions available on it. Opens from a table row click;
// Esc, the close button, and a backdrop click close it.
const RuleDetailDrawer = ({
  rule,
  onClose,
  canViewResource,
  userLabel,
  canRequestExtension,
  userTeams = [],
}: Props) => {
  const { teamId, teamLabel } = useGrantTeam(rule, userTeams);
  const resourceLink = rule ? getResourceLink(rule) : null;
  const requestResourceId = rule
    ? (parseResourceFromExpression(rule.expression)?.id ?? null)
    : null;
  const showRequestExtension =
    canRequestExtension && rule !== null && rule.expires !== null;
  const showViewResource = canViewResource && resourceLink !== null;

  return (
    <StyledDrawer
      anchor="right"
      open={!!rule}
      onClose={onClose}
      PaperProps={{ "aria-label": "Rule details" }}
    >
      {rule && (
        <>
          <DrawerHeader>
            <HeaderText>
              <ActionTitle>{rule.action}</ActionTitle>
              <ExpressionText variant="body2" color="text.secondary">
                {rule.expression}
                <CopyButton
                  text={rule.expression}
                  tooltipText="Copy expression"
                />
              </ExpressionText>
            </HeaderText>
            <IconButton aria-label="Close details" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </DrawerHeader>
          <Divider />

          <Section>
            <SectionLabel>Grant chain</SectionLabel>
            <GrantChain
              rule={rule}
              userLabel={userLabel}
              teamLabel={teamLabel}
            />
            <GrantTeamMembers teamId={teamId} />
          </Section>
          <Divider />

          <Section>
            <SectionLabel>Attributes</SectionLabel>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Policy ID
              </Typography>
              <ValueWithCopy>
                <Typography variant="body2" noWrap>
                  <Mono>{rule.policyId}</Mono>
                </Typography>
                <CopyButton text={rule.policyId} tooltipText="Copy policy ID" />
              </ValueWithCopy>
            </AttrRow>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Granted by
              </Typography>
              <Typography variant="body2">{rule.creator ?? "—"}</Typography>
            </AttrRow>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Granted on
              </Typography>
              <Typography variant="body2">
                {rule.created ? formatDateTimeString(rule.created) : "—"}
              </Typography>
            </AttrRow>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Expires
              </Typography>
              <ExpiresValue expires={rule.expires} />
            </AttrRow>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Cascades
              </Typography>
              <Typography variant="body2">
                {rule.cascades ? "Yes" : "No"}
              </Typography>
            </AttrRow>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Shareable
              </Typography>
              <Typography variant="body2">
                {rule.shareable ? "Yes" : "No"}
              </Typography>
            </AttrRow>
            <AttrRow>
              <Typography variant="body2" color="text.secondary">
                Scope
              </Typography>
              <Typography variant="body2">{rule.scope || "—"}</Typography>
            </AttrRow>
          </Section>

          {(showRequestExtension || showViewResource) && (
            <Footer component="footer">
              {showRequestExtension && (
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={getAccessRequestUrl(requestResourceId)}
                  state={{ canGoBack: true }}
                >
                  Request extension
                </Button>
              )}
              {showViewResource && (
                <Button
                  variant="outlined"
                  color="primary"
                  component={Link}
                  to={resourceLink}
                  startIcon={<OpenInNew fontSize="small" />}
                >
                  View resource
                </Button>
              )}
            </Footer>
          )}
        </>
      )}
    </StyledDrawer>
  );
};

export default RuleDetailDrawer;
